<template>
  <div class="notes-view">
    <v-fade-transition mode="out-in">
      <!-- Note Editor -->
      <div v-if="editingNote" 
           key="editor" 
           ref="editorContainer" 
           class="note-editor-container" 
           :class="{ 
             'is-dragging-mode': draggingBlockIndex !== null,
             'is-uploading-files': isDragging
           }"
           @paste="handlePaste">
        <div v-if="isDragging" class="drag-overlay d-flex align-center justify-center" style="pointer-events: none;">
          <div class="drag-indicator-hint pa-4 rounded-pill bg-black-05 border border-white-05 text-center shadow-lg" style="pointer-events: auto; backdrop-filter: blur(8px);">
            <v-icon icon="mdi-cloud-upload" color="primary" class="mr-2"></v-icon>
            <span class="text-caption font-weight-black text-uppercase letter-spacing-1">Drop images to insert</span>
          </div>
        </div>

        <div class="d-flex align-center ga-4 mb-8">
          <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="handleBackClick" class="px-0 text-none text-grey">Back</v-btn>
          <v-spacer></v-spacer>
          <v-chip v-if="activeNote.isDraft" color="warning" variant="elevated" rounded="pill" class="font-weight-black px-8" style="min-width: 140px; height: 36px; display: flex; justify-content: center;">
            <v-icon icon="mdi-pencil-lock" start size="small"></v-icon>
            DRAFT
          </v-chip>
          <v-btn v-if="activeNote.rkey" color="error" variant="text" rounded="pill" class="px-8 font-weight-bold" @click="promptDeleteCurrentNote">Delete Note</v-btn>
          <v-btn variant="outlined" rounded="pill" class="text-none border-white px-8 font-weight-bold" style="min-width: 140px;" :loading="saving" @click="saveNote(true)">Save Draft</v-btn>
          <v-btn color="primary" variant="flat" rounded="pill" class="px-8 font-weight-black" style="min-width: 140px;" :loading="saving" @click="saveNote(false)">
            Publish
          </v-btn>
        </div>

        <!-- Floating Format Toolbar -->
        <Teleport to="body">
          <v-fade-transition>
            <div v-if="formatToolbar.visible" class="format-toolbar-floating bg-grey-darken-4 rounded-lg elevation-8 px-2 py-1 d-flex align-center" :style="{ top: formatToolbar.top + 'px', left: formatToolbar.left + 'px' }">
              <template v-if="!formatToolbar.showLinkInput">
                <v-btn icon="mdi-format-bold" variant="text" size="x-small" color="white" @mousedown.prevent="handleToolbarFormat('**')"></v-btn>
                <v-btn icon="mdi-format-italic" variant="text" size="x-small" color="white" @mousedown.prevent="handleToolbarFormat('*')"></v-btn>
                <v-btn icon="mdi-format-underline" variant="text" size="x-small" color="white" @mousedown.prevent="handleToolbarFormat('__')"></v-btn>
                <v-btn icon="mdi-format-strikethrough" variant="text" size="x-small" color="white" @mousedown.prevent="handleToolbarFormat('~~')"></v-btn>
                <v-btn icon="mdi-link" variant="text" size="x-small" color="white" @mousedown.prevent="formatToolbar.showLinkInput = true"></v-btn>
              </template>
              <template v-else>
                <v-text-field v-model="formatToolbar.linkUrl" placeholder="Paste link..." density="compact" variant="plain" hide-details class="link-input mx-2" @keyup.enter="applyLink" autofocus></v-text-field>
                <v-btn icon="mdi-check" variant="text" size="x-small" color="primary" @click="applyLink"></v-btn>
                <v-btn icon="mdi-close" variant="text" size="x-small" color="white" @click="formatToolbar.showLinkInput = false"></v-btn>
              </template>
            </div>
          </v-fade-transition>
        </Teleport>

        <v-textarea
          v-model="activeNote.title"
          placeholder="Title"
          variant="plain"
          auto-grow
          rows="1"
          class="modern-story-title-input mb-10"
          hide-details
        ></v-textarea>

        <div class="blocks-editor mt-8" @dragover.prevent="onEditorDragOver" @drop.stop="handleGlobalDrop">

            <transition-group name="list">
              <div v-for="(block, index) in activeNote.blocks" :key="block.id || index" 
                   class="block-wrapper mb-10 position-relative"
                   :class="{ 'is-dragging-block': draggingBlockIndex === Number(index) }"
                   :id="'note-block-' + Number(index)"
                   :draggable="true"
                   @mouseenter="hoverIndex = Number(index)"
                   @mouseleave="hoverIndex = null"
                   @dragstart="onBlockDragStart($event, Number(index))"
                   @dragover.prevent="onBlockDragOver($event, Number(index))"
                   @drop.stop="onBlockDrop($event, Number(index))"
                   @dragend="onBlockDragEnd">

                <!-- Harmonized Drop Indicators -->
                <template v-if="insertionIndex === Number(index)">
                  <!-- Block Reordering Line -->
                  <div 
                    v-if="draggingBlockIndex !== null && draggingBlockIndex !== Number(index) && draggingBlockIndex !== Number(index) - 1" 
                    class="drop-line drop-line-top"
                  ></div>
                  <!-- File Drop Placeholder -->
                  <div v-if="isDragging" class="file-drop-placeholder">
                    <div class="placeholder-content">
                      <v-icon icon="mdi-image-plus" color="primary" class="mb-1"></v-icon>
                      <div class="text-caption font-weight-black text-primary text-uppercase">Insert Image</div>
                    </div>
                  </div>
                </template>

                <!-- Final Bottom Indicator (only for last item) -->
                <template v-if="Number(index) === activeNote.blocks.length - 1 && insertionIndex === activeNote.blocks.length">
                  <!-- Block Reordering Line -->
                  <div 
                    v-if="draggingBlockIndex !== null && draggingBlockIndex !== activeNote.blocks.length - 1" 
                    class="drop-line drop-line-bottom"
                  ></div>
                  <!-- File Drop Placeholder -->
                  <div v-if="isDragging" class="file-drop-placeholder mt-4">
                    <div class="placeholder-content">
                      <v-icon icon="mdi-image-plus" color="primary" class="mb-1"></v-icon>
                      <div class="text-caption font-weight-black text-primary text-uppercase">Insert Image</div>
                    </div>
                  </div>
                </template>
                
                <!-- Left Side Controls (Horizontal Layout) -->
                <div class="side-controls d-flex align-center ga-1">
                  <v-btn icon="mdi-trash-can-outline" variant="text" size="x-small" color="error" class="opacity-80" @click="removeNoteBlock(Number(index))" title="Remove Block"></v-btn>
                  
                  <v-menu location="bottom center">
                    <template v-slot:activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-swap-horizontal" variant="text" size="x-small" color="secondary" title="Change Type"></v-btn>
                    </template>
                    <v-list density="compact" class="rounded-lg bg-surface-dark border border-white-05">
                      <v-list-item v-for="type in blockTypes" :key="type.value" :value="type.value" @click="changeBlockType(Number(index), type.value)" :active="block.type === type.value">
                        <template v-slot:prepend>
                          <v-icon :icon="type.icon" size="small"></v-icon>
                        </template>
                        <v-list-item-title class="text-caption font-weight-bold ml-2">{{ type.text }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <div class="d-flex flex-column align-center move-controls">
                    <v-btn v-if="Number(index) > 0" icon="mdi-chevron-up" variant="text" size="small" color="secondary" class="move-btn" @click="moveNoteBlock(Number(index), -1)" title="Move Up"></v-btn>
                    <v-icon icon="mdi-drag-variant" size="small" color="secondary" class="drag-handle" title="Drag to Reorder"></v-icon>
                    <v-btn v-if="Number(index) < activeNote.blocks.length - 1" icon="mdi-chevron-down" variant="text" size="small" color="secondary" class="move-btn" @click="moveNoteBlock(Number(index), 1)" title="Move Down"></v-btn>
                  </div>
                </div>

                <div class="block-content-area">
                  <v-textarea
                    v-if="['text', 'h1', 'h2', 'h3', 'quote'].includes(block.type)"
                    v-model="block.content"
                    :placeholder="block.type === 'text' ? 'Tell your story...' : 'Type something...'"
                    variant="plain"
                    auto-grow
                    rows="1"
                    :class="['block-input text-serif', block.type + '-block']"
                    hide-details
                    @mouseup="checkSelection($event, Number(index))"
                    @keyup="checkSelection($event, Number(index))"
                    @keydown.enter.exact.prevent="handleEnter($event, Number(index))"
                    @keydown.backspace="handleBackspace($event, Number(index))"
                    @keydown="handleHotkeys($event, Number(index))"
                  ></v-textarea>

                  <div v-else-if="block.type === 'code'" class="code-block-editor bg-black rounded-lg pa-0 overflow-hidden">
                    <div class="code-block-container editor-mode">
                      <div class="code-lang-wrapper">
                        <v-select v-model="block.language" :items="langs" density="compact" variant="plain" hide-details class="code-lang-select"></v-select>
                      </div>
                      <div class="line-numbers" aria-hidden="true">
                        <div v-for="n in (block.content.split('\n').length)" :key="n">{{ n }}</div>
                      </div>
                      <div class="code-content-wrapper">
                        <pre class="editor-highlight" aria-hidden="true"><code :class="['hljs', 'language-' + (block.language || 'plaintext')]" v-html="highlightCode(block.content + (block.content.endsWith('\n') ? ' ' : ''), block.language)"></code></pre>
                        <textarea
                          v-model="block.content"
                          class="editor-textarea"
                          spellcheck="false"
                          @keydown.tab.prevent="handleCodeTab($event, Number(index))"
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="block.type === 'image'" class="image-block-editor">
                    <v-img :src="resolveNoteImageUrl(block)" class="rounded-lg mb-2 shadow-lg" max-height="600" cover>
                      <template v-slot:placeholder>
                        <v-progress-circular indeterminate></v-progress-circular>
                      </template>
                    </v-img>
                    <v-text-field v-model="block.caption" placeholder="Alt text or caption..." variant="plain" density="compact" hide-details class="text-caption text-center text-grey"></v-text-field>
                  </div>

                  <div v-else-if="block.type === 'spacer'" class="spacer-block-editor py-8 text-center">
                    <v-icon icon="mdi-dots-horizontal" size="large" color="grey"></v-icon>
                  </div>
                </div>

                <!-- Bottom Add Content Bar -->
                <div class="add-content-bar d-flex align-center justify-center mt-4">
                  <div class="add-buttons-container d-flex align-center ga-2">
                    <v-btn icon="mdi-plus" variant="text" size="x-small" color="secondary" class="add-btn" @click="addNoteBlock('text', Number(index) + 1)" title="Add Text"></v-btn>
                    <v-btn icon="mdi-format-header-1" variant="text" size="x-small" color="secondary" class="add-btn" @click="addNoteBlock('h1', Number(index) + 1)" title="Add Heading 1"></v-btn>
                    <v-btn icon="mdi-format-header-2" variant="text" size="x-small" color="secondary" class="add-btn" @click="addNoteBlock('h2', Number(index) + 1)" title="Add Heading 2"></v-btn>
                    <v-btn icon="mdi-format-header-3" variant="text" size="x-small" color="secondary" class="add-btn" @click="addNoteBlock('h3', Number(index) + 1)" title="Add Heading 3"></v-btn>
                    <v-btn icon="mdi-format-quote-close" variant="text" size="x-small" color="secondary" class="add-btn" @click="addNoteBlock('quote', Number(index) + 1)" title="Add Quote"></v-btn>
                    <v-btn icon="mdi-code-tags" variant="text" size="x-small" color="secondary" class="add-btn" @click="addNoteBlock('code', Number(index) + 1)" title="Add Code"></v-btn>
                    <v-btn icon="mdi-camera-plus-outline" variant="text" size="x-small" color="secondary" class="add-btn" @click="triggerImageUpload(Number(index) + 1)" title="Add Image"></v-btn>
                    <v-btn icon="mdi-minus" variant="text" size="x-small" color="secondary" class="add-btn" @click="addNoteBlock('spacer', Number(index) + 1)" title="Add Spacer"></v-btn>
                  </div>
                </div>
              </div>
            </transition-group>
          </div>

        <input type="file" ref="noteImageInput" class="d-none" accept="image/*" @change="onNoteFileSelected" />
      </div>

      <!-- Detail View -->
      <div v-else-if="selectedNote" key="detail" class="note-detail-container">
        <div class="d-flex align-center ga-3 mb-16">
          <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="handleBackClick" class="px-0 text-none text-grey">All Notes</v-btn>
          <v-spacer></v-spacer>
          <template v-if="canEdit">
            <v-btn 
              prepend-icon="mdi-pencil" 
              variant="text" 
              class="text-none rounded-pill"
              @click="editExistingNote(selectedNote)"
            >
              Edit
            </v-btn>
          </template>
          <v-btn
            v-if="canEdit && !selectedNote.value.threadUri"
            prepend-icon="mdi-forum-plus-outline"
            color="primary"
            variant="tonal"
            class="rounded-pill px-4 text-none font-weight-black"
            @click="promptActivateDiscussion"
          >
            Activate Discussion
          </v-btn>
          <v-btn
            prepend-icon="mdi-share-variant"
            variant="tonal"
            class="rounded-pill px-4 text-none font-weight-black"
            color="primary"
            @click="shareToBluesky"
          >
            Share
          </v-btn>
        </div>

        <article class="medium-article">
          <header class="mb-10">
            <div v-if="selectedNote.value.isDraft" class="d-flex align-center mb-10">
              <v-chip color="warning" variant="elevated" rounded="pill" class="font-weight-black px-8" style="min-width: 140px; height: 36px; display: flex; justify-content: center;">
                <v-icon icon="mdi-pencil-lock" start size="small"></v-icon>
                DRAFT
              </v-chip>
            </div>
            <h1 class="modern-story-title mb-8">{{ selectedNote.value.title }}</h1>
            <div class="d-flex align-center text-caption text-grey font-weight-medium border-b pb-8">
              <v-avatar 
                size="44" 
                class="mr-3 bg-black-05 border border-white-05 cursor-pointer"
                @click="router.push(`/profile/${selectedNote.author.handle}`)"
              >
                <v-img :src="userAvatar" v-if="userAvatar"></v-img>
                <v-icon v-else icon="mdi-account"></v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div 
                  class="text-white font-weight-bold text-h6 cursor-pointer hover-link"
                  @click="router.push(`/profile/${selectedNote.author.handle}`)"
                >{{ userName }}</div>
                <div class="d-flex align-center">
                  <span :title="new Date(selectedNote.value.createdAt).toLocaleString()">{{ formatDate(selectedNote.value.createdAt) }}</span>
                  <v-icon icon="mdi-circle-small" size="small"></v-icon>
                  <span>5 min read</span>
                </div>
              </div>
            </div>

            <!-- Medium-style Engagement Bar -->
            <div class="engagement-bar d-flex align-center py-4 border-b">
              <div class="d-flex align-center mr-6 engagement-item" :class="{ 'active': hasLiked }">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  :color="hasLiked ? 'error' : 'grey'"
                  @click="handleLikeToggle"
                  :loading="liking"
                >
                  <v-icon :icon="hasLiked ? 'mdi-heart' : 'mdi-heart-outline'"></v-icon>
                </v-btn>
                <span class="engagement-count">{{ formatNumber(likeCount) }}</span>
              </div>

              <div class="d-flex align-center mr-6 engagement-item">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  color="grey"
                  @click="scrollToDiscussion"
                >
                  <v-icon icon="mdi-comment-outline"></v-icon>
                </v-btn>
                <span class="engagement-count">{{ formatNumber(replyCount) }}</span>
              </div>

              <v-spacer></v-spacer>

              <v-btn
                icon
                variant="text"
                size="small"
                :color="isBookmarked ? 'primary' : 'grey'"
                :loading="bookmarking"
                @click.stop
              >
                <v-icon :icon="isBookmarked ? 'mdi-bookmark' : 'mdi-bookmark-outline'"></v-icon>
                
                <v-menu 
                  v-model="showBookmarkMenu"
                  activator="parent" 
                  transition="slide-y-transition" 
                  location="bottom end"
                  :close-on-content-click="false"
                  @update:model-value="onBookmarkMenuUpdate"
                >
                  <v-list density="compact" class="rounded-xl border border-white-05 bg-surface-variant backdrop-blur pa-2 shadow-2xl" min-width="220">
                    <div class="px-3 py-2 text-overline text-grey-lighten-1">Bookmark Groups</div>
                    <v-list-item
                      v-for="group in bookmarkGroups"
                      :key="group.id"
                      @click="toggleNoteInGroup(group)"
                      class="rounded-lg mb-1"
                    >
                      <template v-slot:prepend>
                        <v-checkbox-btn
                          :model-value="isNoteInGroup(group)"
                          readonly
                          color="primary"
                          density="compact"
                          class="mr-2"
                        ></v-checkbox-btn>
                      </template>
                      <v-list-item-title class="font-weight-bold text-white">{{ group.name }}</v-list-item-title>
                      <v-list-item-subtitle class="text-grey-lighten-2">{{ group.postUris.length }} posts</v-list-item-subtitle>
                    </v-list-item>

                    <v-divider class="my-2 border-white-05"></v-divider>
                    
                    <v-list-item
                      prepend-icon="mdi-plus"
                      title="New Group"
                      @click="showCreateGroupDialog = true; showBookmarkMenu = false"
                      class="rounded-lg text-white"
                    ></v-list-item>
                  </v-list>
                </v-menu>
              </v-btn>

              <v-btn
                icon="mdi-share-variant-outline"
                variant="text"
                size="small"
                color="grey"
                @click="shareToBluesky"
              ></v-btn>
            </div>
          </header>

          <div class="article-body text-serif" style="max-width: 680px; margin: 0 auto;">
            <div v-for="(block, index) in selectedNote.value.blocks" :key="index" class="mb-8 block-render">
              <div v-if="block.type === 'text'" class="story-p" v-html="formatText(block.content)"></div>
              <h1 v-else-if="block.type === 'h1'" class="story-h1">{{ block.content }}</h1>
              <h2 v-else-if="block.type === 'h2'" class="story-h2">{{ block.content }}</h2>
              <h3 v-else-if="block.type === 'h3'" class="story-h3">{{ block.content }}</h3>
              <blockquote v-else-if="block.type === 'quote'" class="story-quote">
                <p>{{ block.content }}</p>
              </blockquote>
              <div v-else-if="block.type === 'spacer'" class="story-spacer">
                <v-icon icon="mdi-dots-horizontal" color="grey"></v-icon>
              </div>
              <div v-else-if="block.type === 'code'" class="story-code">
                <div class="code-block-container view-mode">
                  <div v-if="block.language" class="code-lang-label">{{ block.language }}</div>
                  <div class="line-numbers" aria-hidden="true">
                    <div v-for="n in (block.content.split('\n').length)" :key="n">{{ n }}</div>
                  </div>
                  <div class="code-content-wrapper">
                    <pre><code :class="['hljs', 'language-' + (block.language || 'plaintext')]" v-html="highlightCode(block.content, block.language)"></code></pre>
                  </div>
                </div>
              </div>
              <div v-else-if="block.type === 'image'" class="story-image mb-10">
                <v-img :src="resolveImageUrl(block)" class="rounded shadow-xl" max-height="800" cover></v-img>
                <div v-if="block.caption" class="text-caption text-center text-grey mt-2">{{ block.caption }}</div>
              </div>
            </div>

            <!-- End of Content Activation Button -->
            <div v-if="canEdit && !selectedNote.value.threadUri" class="mt-12 mb-8 text-center pt-8 border-t border-white-05">
              <v-btn
                size="x-large"
                color="primary"
                variant="flat"
                class="rounded-pill px-10 text-none font-weight-black shadow-lg"
                prepend-icon="mdi-forum-plus-outline"
                @click="promptActivateDiscussion"
              >
                Activate Discussion for this Story
              </v-btn>
              <p class="text-caption text-grey mt-3">Enable community discussion thread for this story on BlueSky.</p>
            </div>
          </div>

          <!-- Discussion Section -->
          <div v-if="selectedNote && selectedNote.value.threadUri" class="mt-16 pt-10 border-t border-white-05">
            <NoteDiscussion :post-uri="selectedNote.value.threadUri" @back="handleNoteDiscoveryDeleted" @login="$emit('login')" />
          </div>
        </article>
      </div>

      <!-- List View -->
      <div v-else key="list" class="note-list-container">
        <div class="d-flex align-center justify-space-between mb-8">
          <h2 class="text-h4 font-weight-black">Gamedev Stories</h2>
          <v-btn v-if="localIsOwnProfile" prepend-icon="mdi-plus" color="primary" rounded="pill" class="text-none font-weight-black" @click="startNewNote">
            Write a Story
          </v-btn>
        </div>

        <div v-if="loading" class="text-center py-12">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-4 text-grey">{{ currentLoadingMessage }}</p>
        </div>

        <div v-else-if="filteredNotes.length > 0">
          <v-row>
            <v-col v-for="note in filteredNotes" :key="note.uri" cols="12">
              <div class="note-feed-item cursor-pointer mb-8" @click="handleNoteClick(note)">
                <div class="note-feed-item-header d-flex align-center mb-5">
                  <v-avatar size="24" class="mr-2 bg-black-05 border border-white-05">
                    <v-img :src="userAvatar" v-if="userAvatar"></v-img>
                    <v-icon v-else icon="mdi-account" size="small"></v-icon>
                  </v-avatar>
                  <span class="text-caption font-weight-bold text-white">{{ userName }}</span>
                  <v-icon icon="mdi-circle-small" color="grey" size="small"></v-icon>
                  <span class="text-caption text-grey">{{ formatDate(note.value.createdAt) }}</span>
                  <v-spacer></v-spacer>
                  <div v-if="note.value.isDraft" class="d-flex align-center">
                    <v-chip size="x-small" color="warning" variant="elevated" class="font-weight-black px-2">
                      <v-icon icon="mdi-pencil-lock" start size="x-small"></v-icon>
                      DRAFT
                    </v-chip>
                  </div>
                </div>

                <div class="note-feed-item-content d-flex align-start ga-6">
                  <div class="flex-grow-1 min-width-0">
                    <h3 class="text-h5 font-weight-black mb-2 text-serif-bold line-clamp-2">{{ note.value.title }}</h3>
                    <p class="text-body-1 text-grey-lighten-1 line-clamp-2 text-serif-regular mb-4">
                      {{ getSnippet(note) }}
                    </p>
                    
                    <div class="note-feed-item-footer d-flex align-center mt-4">
                      <div class="d-flex align-center mr-4 text-grey text-caption">
                        <v-icon icon="mdi-star" color="amber" size="x-small" class="mr-1"></v-icon>
                        <span>5 min read</span>
                      </div>
                      <v-spacer></v-spacer>
                      <div class="d-flex align-center ga-1">
                        <v-btn icon="mdi-bookmark-outline" variant="text" size="x-small" color="grey" class="action-btn"></v-btn>
                      </div>
                    </div>
                  </div>
                  <div v-if="getFirstImage(note)" class="note-feed-thumb flex-shrink-0">
                    <v-img :src="getFirstImage(note)" cover class="rounded-lg shadow-md" width="160" height="106" aspect-ratio="1.5"></v-img>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <div v-else class="text-center py-16 opacity-50">
          <v-icon icon="mdi-book-open-variant" size="80" class="mb-6"></v-icon>
          <h3 class="text-h5">No stories yet</h3>
          <p class="mt-2">Check back later or start writing your own!</p>
        </div>
      </div>
    </v-fade-transition>

    <v-overlay :model-value="saving" persistent class="align-center justify-center" scrim="#000" opacity="0.9">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <div class="mt-4 text-h6 font-weight-black">Updating your story...</div>
      </div>
    </v-overlay>

    <!-- Activate Discussion Warning Dialog -->
    <v-dialog v-model="showActivateDialog" max-width="500px">
      <v-card class="rounded-xl border border-white-05 bg-surface-dark elevation-24">
        <v-card-title class="text-h6 font-weight-black pa-6 pb-2">
          <v-icon icon="mdi-forum-outline" color="primary" class="mr-2"></v-icon>
          Activate Discussion?
        </v-card-title>
        <v-card-text class="pa-6 pt-0 text-grey-lighten-1">
          <p class="mb-4">This will share your note to BlueSky to create a discussion thread.</p>
          <v-alert
            type="warning"
            variant="tonal"
            border="start"
            class="mb-0"
            icon="mdi-alert-circle-outline"
          >
            <strong>Note:</strong> This action is irreversible unless you delete the post on BlueSky to stop the discussion.
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="text-none font-weight-bold" @click="showActivateDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" class="rounded-pill px-6 font-weight-black text-none" @click="startActivationFlow">
            Activate Now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" timeout="3000" :color="snackbarColor" rounded="pill">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" @click="showSnackbar = false" icon="mdi-close"></v-btn>
      </template>
    </v-snackbar>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400" persistent>
      <v-card class="rounded-xl pa-4">
        <v-card-title class="text-h5 font-weight-black d-flex align-center">
          <v-icon icon="mdi-alert-circle" color="error" class="mr-2"></v-icon>
          Delete Note?
        </v-card-title>
        <v-card-text class="text-body-1 py-4">
          Are you sure you want to delete "<strong>{{ deleteDialog.note?.value?.title }}</strong>"? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pt-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="text-none font-weight-bold" rounded="pill" @click="deleteDialog.show = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" class="text-none font-weight-black px-6" rounded="pill" :loading="saving" @click="handleConfirmedDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Bookmark Group Dialog -->
    <v-dialog v-model="showCreateGroupDialog" max-width="400">
      <v-card class="rounded-xl border border-white-05 bg-surface-variant backdrop-blur">
        <v-card-title class="pa-4 font-weight-bold">Create New Bookmark Group</v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-text-field
            v-model="newBookmarkGroupName"
            placeholder="e.g. Research, Inspiration"
            variant="outlined"
            density="compact"
            hide-details
            class="rounded-lg"
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showCreateGroupDialog = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            variant="flat" 
            :disabled="!newBookmarkGroupName.trim()" 
            :loading="creatingGroup"
            @click="handleCreateBookmarkGroup" 
            class="rounded-lg px-6"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { atproto } from '@/services/atproto'
import { uploadBlob as uploadToBluesky, agent, currentUser, getPdsEndpoint, getOtherProfile, isFocusMode, deletePost, bookmarkedUris, bookmarkGroups, getBookmarks, saveBookmarks, likePost, unlikePost } from '@/services/bluesky'
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';
import NoteDiscussion from '@/components/NoteDiscussion.vue';

const props = defineProps<{
  handle?: string;
  rkey?: string;
  isOwnProfile?: boolean;
}>();

const emit = defineEmits(['share-note', 'activate-discussion', 'login']);

const route = useRoute();
const router = useRouter();

const notesData = ref<any[]>([]);
const filteredNotes = computed(() => {
  return notesData.value.filter((n: any) => !n.value.isDraft || localIsOwnProfile.value);
});
const loading = ref(false);
const showBookmarkMenu = ref(false);
const showCreateGroupDialog = ref(false);
const newBookmarkGroupName = ref('');
const creatingGroup = ref(false);
const saving = ref(false);
const selectedNote = ref<any>(null);
const editingNote = ref(false);
const pdsEndpoint = ref<string | null>(null);
const showSnackbar = ref(false);
const snackbarText = ref('');
const snackbarColor = ref('success');
const noteImageInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const dragCounter = ref(0);
const userAvatar = ref<string | null | undefined>(null);
const userName = ref<string>('User');
const resolvedUserDid = ref<string | null>(null);

const localIsOwnProfile = computed(() => {
  // Check if current user matches handle OR isOwnProfile prop is explicitly true
  if (props.isOwnProfile === true) return true;
  if (!props.handle || !currentUser.value) return false;
  
  const h = props.handle.toLowerCase();
  const myHandle = currentUser.value.handle?.toLowerCase();
  const myDid = currentUser.value.did;
  
  return h === myHandle || h === myDid;
});

const canEdit = computed(() => {
  if (localIsOwnProfile.value) return true;
  if (!selectedNote.value || !currentUser.value) return false;
  return selectedNote.value.author?.did === currentUser.value.did;
});

// Dynamic SEO & Browser Title
useHead({
  title: computed(() => {
    if (editingNote.value) {
      return activeNote.title ? `Editing: ${activeNote.title} - Gamedev.id` : 'New Note - Gamedev.id';
    }
    if (selectedNote.value) {
      return `${selectedNote.value.value.title} - Gamedev.id`;
    }
    return 'Stories - Gamedev.id';
  })
});
const hoverIndex = ref<number | null>(null);
const addMenuIndex = ref<number | null>(null);
const editorContainer = ref<HTMLElement | null>(null);
const draggingBlockIndex = ref<number | null>(null);
const dropTargetIndex = ref<number | null>(null);
const dropPosition = ref<'top' | 'bottom'>('top');

const insertionIndex = computed(() => {
  if (dropTargetIndex.value === null) return null;
  return dropPosition.value === 'top' ? dropTargetIndex.value : dropTargetIndex.value + 1;
});

const onWheelDuringDrag = (e: WheelEvent) => {
  if (draggingBlockIndex.value !== null) {
    // Try to take control of the scroll event
    if (e.cancelable) e.preventDefault();
    
    const delta = e.deltaY;
    // Scroll the window
    window.scrollBy(0, delta);
    
    // Fallback: direct manipulation of scrolling element
    const scrollEl = document.scrollingElement || document.documentElement;
    if (scrollEl) {
      scrollEl.scrollTop += delta;
    }
  }
};

onMounted(() => {
  document.addEventListener('wheel', onWheelDuringDrag, { passive: false, capture: true });
  window.addEventListener('dragenter', onDragEnter);
  window.addEventListener('dragleave', onDragLeave);
  window.addEventListener('dragover', onGlobalDragOver);
  window.addEventListener('drop', handleDrop);
});

onUnmounted(() => {
  document.removeEventListener('wheel', onWheelDuringDrag, { capture: true } as any);
  window.removeEventListener('dragenter', onDragEnter);
  window.removeEventListener('dragleave', onDragLeave);
  window.removeEventListener('dragover', onGlobalDragOver);
  window.removeEventListener('drop', handleDrop);
  isFocusMode.value = false;
});

const onGlobalDragOver = (e: DragEvent) => {
  if (e.dataTransfer?.types.includes('Files')) {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }
};

// Engagement stats
const likeCount = ref(0);
const replyCount = ref(0);
const hasLiked = ref(false);
const liking = ref(false);
const selfLikeUri = ref<string | null>(null);

// Bookmarking
const bookmarking = ref(false);
const isBookmarked = computed(() => {
  if (!selectedNote.value) return false;
  return bookmarkedUris.value.has(selectedNote.value.uri);
});

const deleteDialog = reactive({
  show: false,
  note: null as any
});

const showActivateDialog = ref(false);

watch([() => editingNote.value, () => selectedNote.value], ([editing, selected]) => {
  isFocusMode.value = !!(editing || selected);
}, { immediate: true });

const autoScrollVelocity = ref(0);
const autoScrollRequestId = ref<number | null>(null);
const SCROLL_THRESHOLD = 120;
const MAX_SCROLL_SPEED = 15;

const blockTypes = [
  { text: 'Text', value: 'text', icon: 'mdi-text' },
  { text: 'Heading 1', value: 'h1', icon: 'mdi-format-header-1' },
  { text: 'Heading 2', value: 'h2', icon: 'mdi-format-header-2' },
  { text: 'Heading 3', value: 'h3', icon: 'mdi-format-header-3' },
  { text: 'Quote', value: 'quote', icon: 'mdi-format-quote-close' },
  { text: 'Code', value: 'code', icon: 'mdi-code-tags' },
  { text: 'Spacer', value: 'spacer', icon: 'mdi-minus' }
];

const changeBlockType = (idx: number, newType: string) => {
  const block = activeNote.blocks[idx];
  if (block.type === newType) return;
  
  block.type = newType;
  if (newType === 'code' && !block.language) {
    block.language = 'javascript';
  }
};

const activeNote = reactive<any>({
  rkey: '',
  title: '',
  blocks: [],
  createdAt: '',
  isDraft: false
});

const loadingMessages = [
  "Generating terrain...",
  "Spawning NPCs...",
  "Compiling shaders...",
  "Baking lightmaps...",
  "Loading assets...",
  "Reticulating splines...",
  "Summoning bosses...",
  "Placing health potions...",
  "Calibrating hitboxes...",
  "Polishing pixels...",
  "Tuning physics engine...",
  "Calculating pathfinding...",
  "Equipping legendary loot...",
  "Rendering next frame...",
  "Initializing AI behaviors...",
  "Connecting to servers...",
  "Saving progress...",
  "Unlocking achievements...",
  "Leveling up characters...",
  "Designing dungeons...",
  "Writing dialogue trees...",
  "Balancing stats...",
  "Fixing collision bugs...",
  "Taming wild geometry...",
  "Warming up the GPU..."
];

const currentLoadingMessage = ref("Loading assets...");

const fetchNotes = async () => {
  if (!props.handle) {
    loading.value = false;
    return;
  }
  loading.value = true;
  userAvatar.value = null;
  userName.value = 'User';
  currentLoadingMessage.value = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
  try {
    const [fetchedNotes, endpoint, profile] = await Promise.all([
      atproto.getPosts(props.handle),
      getPdsEndpoint(props.handle),
      getOtherProfile(props.handle)
    ]);
    notesData.value = fetchedNotes;
    pdsEndpoint.value = endpoint;
    userAvatar.value = profile?.avatar;
    userName.value = profile?.displayName || profile?.handle || 'User';
    resolvedUserDid.value = profile?.did || null;
  } catch (error) {
    console.error('Failed to fetch notes:', error);
  } finally {
    loading.value = false;
  }
};

const startNewNote = (initialText?: any) => {
  activeNote.rkey = '';
  activeNote.title = '';
  activeNote.blocks = [{ 
    id: Math.random().toString(36).substr(2, 9), 
    type: 'text', 
    content: (typeof initialText === 'string') ? initialText : '' 
  }];
  activeNote.createdAt = new Date().toISOString();
  activeNote.isDraft = true;
  editingNote.value = true;
  selectedNote.value = null;
};



const editExistingNote = (note: any) => {
  activeNote.rkey = note.uri.split('/').pop();
  activeNote.title = note.value.title;
  // Ensure every block has an ID for animations
  activeNote.blocks = note.value.blocks.map((b: any) => ({ 
    id: b.id || Math.random().toString(36).substr(2, 9),
    ...b 
  }));
  activeNote.createdAt = note.value.createdAt;
  activeNote.isDraft = note.value.isDraft || false;
  editingNote.value = true;
};

const getCaretCoordinates = (element: HTMLTextAreaElement, position: number) => {
  const div = document.createElement('div');
  const style = window.getComputedStyle(element);
  
  const properties = [
    'direction', 'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderStyle',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize', 'fontSizeAdjust', 'lineHeight', 'fontFamily',
    'textAlign', 'textTransform', 'textIndent', 'textDecoration', 'letterSpacing', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordBreak'
  ];

  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordBreak = 'break-word';

  properties.forEach(prop => {
    (div.style as any)[prop] = style.getPropertyValue(prop);
  });

  // Exhaustive match of the textarea metrics
  div.style.width = style.width;
  div.style.height = 'auto';

  if (element.scrollHeight > element.clientHeight) div.style.overflowY = 'scroll';
  else div.style.overflowY = 'hidden';

  div.textContent = element.value.substring(0, position);

  const span = document.createElement('span');
  span.textContent = element.value.substring(position) || '.';
  div.appendChild(span);

  document.body.appendChild(div);
  const { offsetLeft: left, offsetTop: top } = span;
  document.body.removeChild(div);

  return { top, left };
};

const saveNote = async (isDraft = false) => {
  if (!activeNote.title.trim()) {
    snackbarText.value = 'Please enter a title for your story';
    snackbarColor.value = 'error';
    showSnackbar.value = true;
    return;
  }

  saving.value = true;
  try {
    const processedBlocks = await Promise.all(activeNote.blocks.map(async (block: any) => {
      if (block.type === 'image' && block.pendingFile) {
        const blob = await uploadToBluesky(block.pendingFile, block.pendingFile.type); // Changed to uploadToBluesky
        const { pendingFile, ...rest } = block;
        return { ...rest, blob, content: '' };
      }
      return block;
    }));

    let response;
    if (activeNote.rkey) {
      response = await atproto.updateNote(activeNote.rkey, activeNote.title, processedBlocks, activeNote.createdAt, [], isDraft);
      snackbarText.value = isDraft ? 'Draft saved' : 'Story updated!';
    } else {
      response = await atproto.createNote(activeNote.title, processedBlocks, [], isDraft);
      snackbarText.value = isDraft ? 'Draft saved' : 'Story published!';
    }
    
    await fetchNotes();
    
    if (!isDraft) {
      const uri = response?.data?.uri || response?.uri;
      if (uri) {
        const rkey = uri.split('/').pop();
        
        // Navigate to the note detail view
        router.push(`/profile/${resolveHandle()}/note/${rkey}`);
      }
      editingNote.value = false;
    }
    snackbarColor.value = 'success';
    showSnackbar.value = true;
  } catch (err) {
    console.error('Failed to save note:', err);
    snackbarText.value = 'Failed to save. Please try again.';
    snackbarColor.value = 'error';
    showSnackbar.value = true;
  } finally {
    saving.value = false;
  }
};

const confirmDeleteNote = (note: any) => {
  deleteDialog.note = note;
  deleteDialog.show = true;
};

const handleConfirmedDelete = async () => {
  if (!deleteDialog.note) return;
  const note = deleteDialog.note;
  const rkey = note.uri.split('/').pop();
  saving.value = true;
  try {
    await atproto.deleteNote(rkey);
    await fetchNotes();
    selectedNote.value = null;
    editingNote.value = false;
    deleteDialog.show = false;
    deleteDialog.note = null;
    snackbarText.value = 'Deleted successfully.';
    snackbarColor.value = 'success';
    showSnackbar.value = true;
  } catch (err) {
    console.error('Failed to delete note:', err);
    snackbarText.value = 'Delete failed.';
    snackbarColor.value = 'error';
    showSnackbar.value = true;
  } finally {
    saving.value = false;
  }
};

const checkSelection = (e: any, idx: number) => {
  const el = e.target as HTMLTextAreaElement;
  setTimeout(() => {
    if (el && el.selectionStart !== el.selectionEnd) {
      const startPos = el.selectionStart;
      const endPos = el.selectionEnd;
      const selectionEnd = endPos;
      
      const rect = el.getBoundingClientRect();
      const coords = getCaretCoordinates(el, selectionEnd);
      
      formatToolbar.visible = true;
      
      let top, left;

      if (e.type === 'mouseup' && e.clientY) {
        // Mouse precision is best using clientY
        top = e.clientY - 60;
        left = e.clientX - 80;
      } else {
        // Fallback for keyboard or programmatic selection
        top = rect.top + coords.top - el.scrollTop - 60;
        left = rect.left + coords.left - 80;
      }

      // Viewport boundary checks
      const padding = 10;
      const toolbarWidth = 160; // Streamlined width
      if (left < padding) left = padding;
      if (left + toolbarWidth > window.innerWidth - padding) left = window.innerWidth - toolbarWidth - padding;
      if (top < padding) top = padding;

      formatToolbar.top = top;
      formatToolbar.left = left;
      formatToolbar.blockIndex = idx;
    } else {
      if (!formatToolbar.showLinkInput) formatToolbar.visible = false;
    }
  }, 10);
};

const handleToolbarFormat = (wrap: string) => {
  const idx = formatToolbar.blockIndex;
  // Use precise ID targeting: the .block-input class is on the v-input wrapper, so we look for the textarea inside it.
  const el = document.querySelector(`#note-block-${idx} .block-input textarea`) as HTMLTextAreaElement;
  if (!el) return;

  const start = el.selectionStart;
  const end = el.selectionEnd;
  const val = activeNote.blocks[idx].content;
  
  activeNote.blocks[idx].content = val.substring(0, start) + wrap + val.substring(start, end) + wrap + val.substring(end);
  formatToolbar.visible = false;
};

const applyLink = () => {
  const idx = formatToolbar.blockIndex;
  // Use precise ID targeting
  const el = document.querySelector(`#note-block-${idx} .block-input textarea`) as HTMLTextAreaElement;
  if (!el) return;

  const start = el.selectionStart;
  const end = el.selectionEnd;
  const val = activeNote.blocks[idx].content;
  const label = val.substring(start, end) || 'link';
  const url = formatToolbar.linkUrl || 'https://';
  
  activeNote.blocks[idx].content = val.substring(0, start) + `[${label}](${url})` + val.substring(end);
  formatToolbar.visible = false;
  formatToolbar.showLinkInput = false;
  formatToolbar.linkUrl = '';
};



const handleEnter = (e: any, idx: number) => {
  const el = e.target;
  const pos = el.selectionStart;
  const val = activeNote.blocks[idx].content;
  
  const before = val.substring(0, pos);
  const after = val.substring(pos);
  
  activeNote.blocks[idx].content = before;
  addNoteBlock('text', idx + 1);
  activeNote.blocks[idx + 1].content = after;
  
  setTimeout(() => {
    const nextTextarea = document.querySelector(`#note-block-${idx + 1} textarea`) as HTMLElement;
    nextTextarea?.focus();
  }, 50);
};

const handleHotkeys = (e: KeyboardEvent, idx: number) => {
  if (e.ctrlKey || e.metaKey) {
    if (e.key === 'b' || e.key === 'B') {
      e.preventDefault();
      formatToolbar.blockIndex = idx;
      handleToolbarFormat('**');
    } else if (e.key === 'i' || e.key === 'I') {
      e.preventDefault();
      formatToolbar.blockIndex = idx;
      handleToolbarFormat('*');
    } else if (e.key === 'u' || e.key === 'U') {
      e.preventDefault();
      formatToolbar.blockIndex = idx;
      handleToolbarFormat('__');
    }
  }
};

const handleCodeTab = (e: KeyboardEvent, idx: number) => {
  const el = e.target as HTMLTextAreaElement;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const val = activeNote.blocks[idx].content;

  if (start !== end) {
    // Multi-line selection logic
    const textBefore = val.substring(0, start);
    const lineStart = textBefore.lastIndexOf('\n') + 1;
    const textAfter = val.substring(end);
    const lineEnd = end + (val.substring(end).indexOf('\n') !== -1 ? val.substring(end).indexOf('\n') : val.substring(end).length);
    
    const selectedText = val.substring(lineStart, lineEnd);
    const lines = selectedText.split('\n');
    let newText = '';
    let totalShift = 0;

    if (e.shiftKey) {
      // De-indent lines
      newText = lines.map((line: string) => {
        if (line.startsWith('  ')) {
          totalShift -= 2;
          return line.substring(2);
        } else if (line.startsWith(' ')) {
          totalShift -= 1;
          return line.substring(1);
        }
        return line;
      }).join('\n');
    } else {
      // Indent lines
      newText = lines.map((line: string) => {
        totalShift += 2;
        return '  ' + line;
      }).join('\n');
    }

    activeNote.blocks[idx].content = val.substring(0, lineStart) + newText + val.substring(lineEnd);

    setTimeout(() => {
      // Keep the lines selected
      el.selectionStart = lineStart;
      el.selectionEnd = lineStart + newText.length;
    }, 0);
    return;
  }

  if (e.shiftKey) {
    // Improved De-indent (Single Line): Remove indentation from the start of the current line
    const textBefore = val.substring(0, start);
    const lineStart = textBefore.lastIndexOf('\n') + 1;
    const lineContent = val.substring(lineStart);
    
    let shift = 0;
    if (lineContent.startsWith('  ')) {
      activeNote.blocks[idx].content = val.substring(0, lineStart) + val.substring(lineStart + 2);
      shift = 2;
    } else if (lineContent.startsWith(' ')) {
      activeNote.blocks[idx].content = val.substring(0, lineStart) + val.substring(lineStart + 1);
      shift = 1;
    }

    if (shift > 0) {
      setTimeout(() => {
        // Adjust cursor but don't move it past the line start
        const newPos = Math.max(lineStart, start - shift);
        el.selectionStart = el.selectionEnd = newPos;
      }, 0);
    }
  } else {
    // Indent: Insert two spaces at cursor
    activeNote.blocks[idx].content = val.substring(0, start) + '  ' + val.substring(end);

    // Maintain cursor position after the inserted spaces
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + 2;
    }, 0);
  }
};


const handleBackspace = (e: any, idx: number) => {
  if (idx === 0) return;
  const el = e.target;
  if (el.selectionStart === 0 && el.selectionEnd === 0 && !activeNote.blocks[idx].content) {
    e.preventDefault();
    const prevContent = activeNote.blocks[idx - 1].content || '';
    removeNoteBlock(idx);
    setTimeout(() => {
      const prevEl = document.querySelector(`#note-block-${idx - 1} textarea`) as HTMLTextAreaElement;
      if (prevEl) {
        prevEl.focus();
        prevEl.selectionStart = prevEl.selectionEnd = prevContent.length;
      }
    }, 50);
  }
};


const removeNoteBlock = (idx: number) => {
  if (activeNote.blocks.length > 1) {
    activeNote.blocks.splice(idx, 1);
  } else {
    activeNote.blocks[0] = { id: Math.random().toString(36).substr(2, 9), type: 'text', content: '' };
  }
};

const moveNoteBlock = (idx: number, dir: number) => {
  const newIdx = idx + dir;
  if (newIdx < 0 || newIdx >= activeNote.blocks.length) return;
  const item = activeNote.blocks.splice(idx, 1)[0];
  activeNote.blocks.splice(newIdx, 0, item);
};

const triggerImageUpload = (idx: number) => {
  formatToolbar.blockIndex = idx - 1; // Mark where to insert
  noteImageInput.value?.click();
};

const onNoteFileSelected = (e: any) => {
  const file = e.target.files?.[0];
  if (!file) return;
  handleImageInsert(file, formatToolbar.blockIndex + 1);
  e.target.value = '';
};

const handleImageInsert = (file: File, idx: number) => {
  activeNote.blocks.splice(idx, 0, {
    type: 'image',
    content: URL.createObjectURL(file),
    pendingFile: file,
    caption: ''
  });
};

const handlePaste = (e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    
    for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.indexOf('image') !== -1) {
            const file = item.getAsFile()
            if (file) {
                handleImageInsert(file, activeNote.blocks.length) // Assuming handleImageInsert is the correct function
            }
        }
    }
};

const onDragEnter = (e: DragEvent) => {
  if (e.dataTransfer?.types.includes('Files')) {
    e.preventDefault();
    dragCounter.value++;
    isDragging.value = true;
  }
};

const onDragLeave = (e: DragEvent) => {
  if (e.dataTransfer?.types.includes('Files')) {
    dragCounter.value--;
    if (dragCounter.value <= 0) {
      dragCounter.value = 0;
      isDragging.value = false;
    }
  }
};

const onBlockDragStart = (e: DragEvent, idx: number) => {
  draggingBlockIndex.value = idx;
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx.toString());
    
    // Create an invisible drag image to prevent the default ghost image if desired, 
    // but here we keep it for now as it's standard.
  }
};

const startAutoScroll = () => {
  if (autoScrollRequestId.value !== null) return;
  
  const scroll = () => {
    if (autoScrollVelocity.value !== 0) {
      window.scrollBy(0, autoScrollVelocity.value);
    }
    autoScrollRequestId.value = requestAnimationFrame(scroll);
  };
  
  autoScrollRequestId.value = requestAnimationFrame(scroll);
};

const stopAutoScroll = () => {
  if (autoScrollRequestId.value !== null) {
    cancelAnimationFrame(autoScrollRequestId.value);
    autoScrollRequestId.value = null;
  }
  autoScrollVelocity.value = 0;
};

const showAddMenuAt = (idx: number) => {
  addMenuIndex.value = idx;
};

const addNoteBlock = (type: string, idx?: number) => {
  const newBlock = { 
    id: Math.random().toString(36).substr(2, 9),
    type, 
    content: '', 
    language: type === 'code' ? 'javascript' : undefined 
  };
  if (typeof idx === 'number') {
    activeNote.blocks.splice(idx, 0, newBlock);
  } else {
    activeNote.blocks.push(newBlock);
  }
  addMenuIndex.value = null;
};

const langs = ['javascript', 'typescript', 'python', 'html', 'css', 'json', 'bash', 'csharp', 'gdscript'];

const formatToolbar = reactive({
  visible: false,
  top: 0,
  left: 0,
  blockIndex: -1,
  showLinkInput: false,
  linkUrl: ''
});

const updateAutoScrollVelocity = (e: DragEvent) => {
  const { clientY } = e;
  const viewportHeight = window.innerHeight;
  
  if (clientY < SCROLL_THRESHOLD) {
    // Scroll up
    const intensity = (SCROLL_THRESHOLD - clientY) / SCROLL_THRESHOLD;
    autoScrollVelocity.value = -MAX_SCROLL_SPEED * intensity;
    startAutoScroll();
  } else if (clientY > viewportHeight - SCROLL_THRESHOLD) {
    // Scroll down
    const intensity = (clientY - (viewportHeight - SCROLL_THRESHOLD)) / SCROLL_THRESHOLD;
    autoScrollVelocity.value = MAX_SCROLL_SPEED * intensity;
    startAutoScroll();
  } else {
    autoScrollVelocity.value = 0;
    // We don't stop the animation frame immediately to avoid jitter, 
    // it will just scroll 0 pixels. But for performance we could stop it if velocity is 0 for a while.
  }
};

const onBlockDragOver = (e: DragEvent, idx: number) => {
  if (draggingBlockIndex.value === null && !e.dataTransfer?.types.includes('Files')) return;
  e.stopPropagation(); // Prevent container from overriding more specific block detection
  updateAutoScrollVelocity(e);
  
  // Calculate top or bottom half
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const relY = e.clientY - rect.top;
  dropPosition.value = relY < rect.height / 2 ? 'top' : 'bottom';
  
  dropTargetIndex.value = idx;
};

const onEditorDragOver = (e: DragEvent) => {
  if (draggingBlockIndex.value === null && !e.dataTransfer?.types.includes('Files')) return;
  updateAutoScrollVelocity(e);
  // If we are over the container but not a specific block, 
  // we keep the last dropTargetIndex but update the velocity.
};

const onBlockDrop = (e: DragEvent, idx: number) => {
  // Update target just in case drop was triggered on a specific block but dragover didn't catch final position
  dropTargetIndex.value = idx;
  handleGlobalDrop(e);
};

const handleGlobalDrop = (e: DragEvent) => {
  stopAutoScroll();
  
  // If this is a file drop, let handleDrop take care of it
  if (e.dataTransfer?.types.includes('Files')) {
    handleDrop(e);
    return;
  }
  
  // Try to get fromIdx from dataTransfer first, then fallback to ref
  const data = e.dataTransfer?.getData('text/plain');
  const fromIdx = data ? parseInt(data, 10) : draggingBlockIndex.value;
  
  if (fromIdx === null || isNaN(fromIdx) || dropTargetIndex.value === null) {
    draggingBlockIndex.value = null;
    dropTargetIndex.value = null;
    return;
  }
  
  let toIdx = dropTargetIndex.value;
  
  // Adjust toIdx based on drop position (top or bottom half of target)
  if (dropPosition.value === 'bottom') {
    toIdx += 1;
  }
  
  // If moving down, the removal of fromIdx shifts everything, so we adjust the target index
  if (fromIdx < toIdx) {
    toIdx -= 1;
  }

  // Prevent no-op moves
  if (fromIdx === toIdx) {
    draggingBlockIndex.value = null;
    dropTargetIndex.value = null;
    return;
  }

  // Perform the move
  const item = activeNote.blocks.splice(fromIdx, 1)[0];
  activeNote.blocks.splice(toIdx, 0, item);
  
  draggingBlockIndex.value = null;
  dropTargetIndex.value = null;
};

const onBlockDragEnd = () => {
  stopAutoScroll();
  draggingBlockIndex.value = null;
  dropTargetIndex.value = null;
};

const handleDrop = (e: DragEvent) => {
  const isFiles = e.dataTransfer?.types.includes('Files');
  
  if (isFiles) {
    e.preventDefault();
  }
  
  dragCounter.value = 0;
  isDragging.value = false;

  if (!isFiles) {
    draggingBlockIndex.value = null;
    dropTargetIndex.value = null;
    return;
  }

  const files = e.dataTransfer?.files;
  if (!files?.length) return;

  // Use the reordering system's insertionIndex if available, otherwise just append at end
  let targetIdx = insertionIndex.value !== null ? insertionIndex.value : activeNote.blocks.length;

  for (let i = 0; i < files.length; i++) {
    if (files[i].type.startsWith('image/')) {
      handleImageInsert(files[i], targetIdx);
      targetIdx++; // Increment for multiple files
    }
  }

  draggingBlockIndex.value = null;
  dropTargetIndex.value = null;
};

const highlightCode = (code: string, lang?: string) => {
  if (lang && hljs.getLanguage(lang)) {
    return hljs.highlight(code, { language: lang }).value;
  }
  return hljs.highlightAuto(code).value;
};

const formatText = (text: string) => {
  if (!text) return '';
  return text
    .replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([\s\S]*?)\*/g, '<em>$1</em>')
    .replace(/__([\s\S]*?)__/g, '<u>$1</u>')
    .replace(/~~([\s\S]*?)~~/g, '<del>$1</del>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="text-primary">$1</a>')
    .replace(/\n/g, '<br>');
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 0) return 'now'; 
  if (diffInSeconds < 60) return `${diffInSeconds}s`;
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const getSnippet = (note: any) => {
  const allText = note.value.blocks
    .filter((b: any) => ['text', 'h1', 'h2', 'h3', 'quote'].includes(b.type))
    .map((b: any) => b.content)
    .join(' ');
  
  const cleanText = allText
    .replace(/\*\*([\s\S]*?)\*\*/g, '$1')
    .replace(/\*([\s\S]*?)\*/g, '$1')
    .replace(/__([\s\S]*?)__/g, '$1')
    .replace(/~~([\s\S]*?)~~/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/<[^>]*>/g, '')
    .replace(/\n/g, ' ')
    .trim();

  return cleanText.substring(0, 160) + (cleanText.length > 160 ? '...' : '');
};

const getFirstImage = (note: any) => {
  const block = note.value.blocks.find((b: any) => b.type === 'image');
  if (!block) return null;
  return resolveImageUrl(block) || block.content;
};

const resolveNoteImageUrl = (block: any) => {
  if (block.pendingFile) return block.content;
  if (block.blob) {
    const cid = block.blob.ref?.toString() || block.blob.cid;
    const did = resolvedUserDid.value || selectedNote.value?.author?.did || props.handle;
    if (cid && did && did.startsWith('did:')) {
      return `https://cdn.bsky.app/img/feed_fullsize/plain/${did}/${cid}@webp`;
    }
  }
  return block.content;
};

const resolveImageUrl = (block: any) => resolveNoteImageUrl(block);

const resolveHandle = () => props.handle || userName.value || props.handle || 'me';

const handleNoteClick = (note: any) => {
  const rkey = note.uri.split('/').pop();
  router.push(`/profile/${resolveHandle()}/note/${rkey}`);
};

const handleBackClick = () => {
  editingNote.value = false;
  selectedNote.value = null;
  router.push(`/profile/${resolveHandle()}?tab=notes`);
};

const shareToBluesky = () => {
  if (!selectedNote.value) return;
  const rkey = selectedNote.value.uri.split('/').pop();
  emit('share-note', { ...selectedNote.value, rkey });
};

const handleScroll = () => {
  if (formatToolbar.visible && !formatToolbar.showLinkInput) {
    formatToolbar.visible = false;
  }
};

onMounted(() => {
  fetchNotes();
  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', handleScroll);
});

watch(() => props.handle, fetchNotes, { immediate: true });
watch(() => currentUser.value?.did, (newDid) => {
  if (newDid) fetchNotes();
});

const syncSelectedNote = () => {
  const isNoteRoute = route.path.includes('/note/');
  const rkey = route.params.rkey as string;
  
  if (isNoteRoute && rkey) {
    if (filteredNotes.value.length > 0) {
      const note = filteredNotes.value.find((n: any) => n.uri.split('/').pop() === rkey);
      selectedNote.value = note || null;
    }
  } else if (isNoteRoute && !rkey && route.query.initialText && !editingNote.value) {
    // Handle "Make as Note" case: pre-fill story editor with provided text
    startNewNote(route.query.initialText as string);
    // Remove query params to prevent re-triggering if user navigates back
    router.replace({ path: route.path, query: {} });
  } else {
    // Only reset if we're not currently editing a new note (new notes have no rkey)
    if (!editingNote.value) {
      selectedNote.value = null;
      editingNote.value = false;
    }
  }
};

watch(() => route.path, syncSelectedNote, { immediate: true });
watch(() => filteredNotes.value, syncSelectedNote, { deep: true, immediate: true });

const promptActivateDiscussion = () => {
  showActivateDialog.value = true;
};

const startActivationFlow = () => {
  showActivateDialog.value = false;
  if (!selectedNote.value) return;
  const rkey = selectedNote.value.uri.split('/').pop();
  emit('activate-discussion', { ...selectedNote.value, rkey });
};

const handleNoteDiscoveryDeleted = async () => {
  if (!selectedNote.value) return;
  
  const threadUri = selectedNote.value.value?.threadUri;
  
  // Proactive UI update
  if (selectedNote.value.value) {
    selectedNote.value.value.threadUri = null;
  }
  
  saving.value = true;
  try {
    // 1. Delete actual post from BlueSky
    if (threadUri) {
      try {
        await deletePost(threadUri);
      } catch (postErr) {
        console.warn('Failed to delete Bluesky post, continuing with note update:', postErr);
      }
    }

    // 2. Clear threadUri from Note record
    const noteUri = selectedNote.value.uri;
    const rkey = noteUri.split('/').pop();
    const note = await atproto.getNote(noteUri);
    await atproto.updateNote(
      rkey,
      note.value.title,
      note.value.blocks,
      note.value.createdAt,
      note.value.tags,
      note.value.isDraft,
      null
    );
    
    await fetchNotes();
    snackbarText.value = 'Discussion thread deleted.';
    showSnackbar.value = true;
  } catch (e) {
    console.error('Failed to reset discussion:', e);
  } finally {
    saving.value = false;
  }
};

const promptDeleteCurrentNote = () => {
  if (!activeNote.rkey) return;
  const note = notesData.value.find((n: any) => n.uri.split('/').pop() === activeNote.rkey);
  if (note) {
    deleteDialog.note = note;
    deleteDialog.show = true;
  }
};

const confirmDiscussionActivation = async (noteUri: string, threadUri: string) => {
  saving.value = true;
  try {
    const note = await atproto.getNote(noteUri);
    const rkey = noteUri.split('/').pop();
    if (!rkey) return;
    await atproto.updateNote(
      rkey,
      note.value.title,
      note.value.blocks,
      note.value.createdAt,
      note.value.tags,
      note.value.isDraft,
      threadUri
    );
    
    // Explicitly update local state for immediate UI response
    if (selectedNote.value && selectedNote.value.uri === noteUri) {
      if (!selectedNote.value.value) selectedNote.value.value = {};
      selectedNote.value.value.threadUri = threadUri;
    }
    
    await fetchNotes();
    snackbarText.value = 'Discussion activated!';
    snackbarColor.value = 'success';
    showSnackbar.value = true;
  } catch (e) {
    console.error('Failed to activate discussion:', e);
    snackbarText.value = 'Activation failed.';
    snackbarColor.value = 'error';
    showSnackbar.value = true;
  } finally {
    saving.value = false;
  }
};

const formatNumber = (num: number) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const fetchThreadStats = async () => {
  if (!selectedNote.value?.value?.threadUri) {
    likeCount.value = 0;
    replyCount.value = 0;
    hasLiked.value = false;
    return;
  }

  try {
    const thread = await atproto.getPostThread(selectedNote.value.value.threadUri);
    if (thread?.post) {
      likeCount.value = thread.post.likeCount || 0;
      replyCount.value = thread.post.replyCount || 0;
      hasLiked.value = !!thread.post.viewer?.like;
      selfLikeUri.value = thread.post.viewer?.like || null;
    }
  } catch (e) {
    console.error('Failed to fetch thread stats:', e);
  }
};

const handleLikeToggle = async () => {
  console.log('[NotesView] handleLikeToggle clicked');
  if (!selectedNote.value?.value?.threadUri) {
    console.log('[NotesView] handleLikeToggle: No threadUri');
    snackbarText.value = 'Discussion not activated yet';
    snackbarColor.value = 'info';
    showSnackbar.value = true;
    return;
  }

  if (!currentUser.value) {
    console.log('[NotesView] handleLikeToggle: No session, emitting login');
    emit('login');
    return;
  }

  liking.value = true;
  try {
    if (hasLiked.value && selfLikeUri.value) {
      console.log('[NotesView] handleLikeToggle: Unliking');
      await unlikePost(selfLikeUri.value);
      hasLiked.value = false;
      likeCount.value--;
      selfLikeUri.value = null;
    } else {
      console.log('[NotesView] handleLikeToggle: Fetching thread for liking');
      const thread = await atproto.getPostThread(selectedNote.value.value.threadUri);
      if (thread?.post) {
        console.log('[NotesView] handleLikeToggle: Liking post');
        const res = await likePost(thread.post.uri, thread.post.cid);
        hasLiked.value = true;
        likeCount.value++;
        selfLikeUri.value = res.uri;
      }
    }
  } catch (e: any) {
    console.error('[NotesView] Failed to toggle like:', e);
    snackbarText.value = `Failed to update like: ${e.message || 'Unknown error'}`;
    snackbarColor.value = 'error';
    showSnackbar.value = true;
  } finally {
    liking.value = false;
  }
};

const onBookmarkMenuUpdate = async (val: boolean) => {
  if (val && !currentUser.value) {
    showBookmarkMenu.value = false;
    emit('login');
    return;
  }
  if (val && bookmarkGroups.value.length === 0) {
    // Attempt to load if empty
    await getBookmarks();
  }
};

const isNoteInGroup = (group: any) => {
  if (!selectedNote.value) return false;
  return group.postUris?.includes(selectedNote.value.uri);
};

const toggleNoteInGroup = async (group: any) => {
  if (!selectedNote.value || !currentUser.value) return;
  
  bookmarking.value = true;
  try {
    const isInside = isNoteInGroup(group);
    let updatedGroups = JSON.parse(JSON.stringify(bookmarkGroups.value));
    
    updatedGroups = updatedGroups.map((g: any) => {
      if (g.id === group.id) {
        if (isInside) {
          g.postUris = g.postUris.filter((u: string) => u !== selectedNote.value.uri);
        } else {
          g.postUris = [...(g.postUris || []), selectedNote.value.uri];
        }
      }
      return g;
    });

    await saveBookmarks({ groups: updatedGroups });
    
    snackbarText.value = isInside ? 'Removed from bookmarks' : `Added to ${group.name}`;
    snackbarColor.value = 'success';
    showSnackbar.value = true;
  } catch (e: any) {
    console.error('[NotesView] Failed to toggle bookmark in group:', e);
    snackbarText.value = `Failed to update bookmark: ${e.message || 'Unknown error'}`;
    snackbarColor.value = 'error';
    showSnackbar.value = true;
  } finally {
    bookmarking.value = false;
  }
};

const handleCreateBookmarkGroup = async () => {
  const name = newBookmarkGroupName.value.trim();
  if (!name || !selectedNote.value) return;

  creatingGroup.value = true;
  try {
    const newId = Math.random().toString(36).substring(2, 11);
    const updatedGroups = [
      ...JSON.parse(JSON.stringify(bookmarkGroups.value)),
      {
        id: newId,
        name: name,
        postUris: [selectedNote.value.uri]
      }
    ];

    await saveBookmarks({ groups: updatedGroups });
    
    snackbarText.value = `Created group and added to "${name}"`;
    snackbarColor.value = 'success';
    showSnackbar.value = true;
    
    showCreateGroupDialog.value = false;
    newBookmarkGroupName.value = '';
  } catch (e: any) {
    console.error('[NotesView] Failed to create bookmark group:', e);
    snackbarText.value = `Failed to create group: ${e.message || 'Unknown error'}`;
    snackbarColor.value = 'error';
    showSnackbar.value = true;
  } finally {
    creatingGroup.value = false;
  }
};

const toggleBookmark = async () => {
  console.log('[NotesView] toggleBookmark (Legacy) clicked');
  // We keep this for backward compatibility if any other part uses it, 
  // but the UI now uses the menu.
  if (!selectedNote.value) {
    console.log('[NotesView] toggleBookmark: No selectedNote');
    return;
  }
  if (!currentUser.value) {
    console.log('[NotesView] toggleBookmark: No session, emitting login');
    emit('login');
    return;
  }

  bookmarking.value = true;
  try {
    console.log('[NotesView] toggleBookmark: Fetching bookmarks');
    const bookmarks = await getBookmarks();
    if (!bookmarks) {
      console.log('[NotesView] toggleBookmark: getBookmarks returned null');
      snackbarText.value = 'Failed to load bookmarks. Please try again.';
      snackbarColor.value = 'error';
      showSnackbar.value = true;
      return;
    }

    const uri = selectedNote.value.uri;
    let foundGid = -1;
    let foundIdx = -1;

    bookmarks.groups.forEach((g: any, gi: number) => {
      const idx = g.postUris?.indexOf(uri);
      if (idx !== undefined && idx !== -1) {
        foundGid = gi;
        foundIdx = idx;
      }
    });

    if (foundGid !== -1) {
      // Remove
      bookmarks.groups[foundGid].postUris.splice(foundIdx, 1);
    } else {
      // Add to default group or first group
      if (bookmarks.groups.length === 0) {
        bookmarks.groups.push({ name: 'General', postUris: [uri] });
      } else {
        bookmarks.groups[0].postUris = bookmarks.groups[0].postUris || [];
        bookmarks.groups[0].postUris.push(uri);
      }
    }

    await saveBookmarks(bookmarks);
    snackbarText.value = foundGid !== -1 ? 'Removed from bookmarks' : 'Added to bookmarks';
    snackbarColor.value = 'success';
    showSnackbar.value = true;
  } catch (e: any) {
    console.error('Failed to toggle bookmark:', e);
    snackbarText.value = `Failed to update bookmarks: ${e.message || 'Unknown error'}`;
    snackbarColor.value = 'error';
    showSnackbar.value = true;
  } finally {
    bookmarking.value = false;
  }
};

const scrollToDiscussion = () => {
  const el = document.querySelector('.mt-16.pt-10.border-t');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

watch(() => selectedNote.value, (newVal) => {
  if (newVal) {
    fetchThreadStats();
  }
}, { immediate: true });

defineExpose({ 
  startNewNote,
  editingNote,
  refresh: fetchNotes,
  confirmDiscussionActivation
});
</script>

<style scoped>
.notes-view {
  max-width: 840px;
  margin: 0 auto;
  min-height: 80vh;
  padding: 40px 20px;
  position: relative;
}

.note-editor-container, .note-detail-container, .note-list-container {
  max-width: 740px;
  margin: 0 auto;
  position: relative;
}

.text-serif-regular {
  font-family: 'Lora', 'Georgia', serif !important;
  font-weight: 400;
  line-height: 1.6;
}

.text-serif-bold {
  font-family: 'Lora', 'Georgia', serif !important;
  font-weight: 900;
}

.text-serif {
  font-family: 'Lora', 'Georgia', serif !important;
}

.note-editor-container {
  padding-bottom: 200px;
}

.modern-story-title {
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif !important;
  font-size: 3.5rem !important;
  font-weight: 800 !important;
  line-height: 1.15 !important;
  letter-spacing: -0.04em !important;
  color: white;
}

.modern-story-title-input :deep(textarea) {
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif !important;
  font-size: 3.5rem !important;
  font-weight: 800 !important;
  line-height: 1.15 !important;
  letter-spacing: -0.04em !important;
  padding: 0 !important;
}

.note-feed-item {
  transition: opacity 0.2s ease;
}

.note-feed-item:hover {
  opacity: 0.8;
}

.note-feed-item-header {
  opacity: 0.8;
}

.block-wrapper {
  position: relative;
}

/* Expanded hit area for drag and drop on the left side */
.block-wrapper::before {
  content: '';
  position: absolute;
  left: -130px;
  top: -20px;
  bottom: -20px;
  right: -20px;
  z-index: 1; /* Bring to front to catch events */
  border-radius: 16px;
  transition: all 0.2s ease;
  pointer-events: none; /* Default to none so it doesn't block clicks */
}

.note-editor-container.is-dragging-mode .block-wrapper::before {
  pointer-events: auto;
}

.block-content-area {
  position: relative;
  z-index: 2;
}

.side-controls {
  position: absolute;
  left: -120px;
  top: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
  width: auto;
  min-width: 110px;
  z-index: 10;
  padding-top: 0;
}

.move-controls {
  opacity: 0.6;
  gap: 0;
}

.move-controls:hover {
  opacity: 1;
}

.move-btn {
  height: 24px !important;
  min-height: 24px !important;
  width: 24px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.drag-handle {
  height: 20px;
  display: flex;
  align-items: center;
}

.block-wrapper:hover .side-controls {
  opacity: 0.8;
}

.side-controls:hover {
  opacity: 1 !important;
}

.drop-line {
  position: absolute;
  left: -130px;
  right: -20px;
  height: 4px;
  background: rgb(var(--v-theme-primary));
  border-radius: 4px;
  z-index: 100;
  animation: pulse-line 1.5s infinite;
  pointer-events: none;
}

.drop-line::after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  background: white;
  border-radius: 50%;
}

.drop-line-top {
  top: -20px;
}

.drop-line-bottom {
  bottom: -20px;
}

@keyframes pulse-line {
  0% { opacity: 0.6; transform: scaleX(0.99); }
  50% { opacity: 1; transform: scaleX(1); }
  100% { opacity: 0.6; transform: scaleX(0.99); }
}

.is-dragging-block {
  opacity: 0.4;
  outline: 2px dashed rgba(var(--v-theme-primary), 0.5);
  outline-offset: -2px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
}

.block-content-editable {
  flex-grow: 1;
}

.block-input :deep(textarea) {
  font-size: 1.3rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
}

.h1-block :deep(textarea) {
  font-size: 2.2rem !important;
  font-weight: 800;
  line-height: 1.2;
}

.h2-block :deep(textarea) {
  font-size: 1.8rem !important;
  font-weight: 700;
}

.quote-block :deep(textarea) {
  font-size: 1.6rem !important;
  font-style: italic;
  border-left: 3px solid var(--v-theme-primary);
  padding-left: 24px;
  opacity: 0.8;
}

.add-content-bar {
  position: relative;
  opacity: 0;
  transition: opacity 0.2s ease;
  height: 32px;
  margin-top: 10px;
  margin-bottom: -16px;
  z-index: 100;
}

.block-wrapper:hover .add-content-bar {
  opacity: 0.6;
}

.add-content-bar:hover {
  opacity: 1 !important;
}

.add-buttons-container {
  background: rgba(255, 255, 255, 0.05);
  padding: 4px 16px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
}

.add-btn {
  opacity: 0.7;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.add-btn:hover {
  opacity: 1;
  transform: scale(1.2);
}

.format-toolbar-floating {
  position: fixed;
  z-index: 10000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.link-input :deep(input) {
  color: white !important;
  font-size: 0.8rem;
}

.drag-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none; /* Let events pass through to the container */
}

.note-editor-container.is-uploading-files::after {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  border: 4px solid var(--v-theme-primary);
  opacity: 0.15;
  z-index: 9998;
  animation: border-pulse 2s infinite ease-in-out;
}

@keyframes border-pulse {
  0% { opacity: 0.05; }
  50% { opacity: 0.2; }
  100% { opacity: 0.05; }
}

.drag-indicator-hint {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
}

.file-drop-placeholder {
  height: 80px;
  border: 2px dashed rgba(var(--v-theme-primary), 0.3);
  background: rgba(var(--v-theme-primary), 0.05);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 0;
  transition: all 0.2s ease;
  animation: placeholder-pulse 2s infinite ease-in-out;
}

@keyframes placeholder-pulse {
  0% { border-color: rgba(var(--v-theme-primary), 0.2); background: rgba(var(--v-theme-primary), 0.03); }
  50% { border-color: rgba(var(--v-theme-primary), 0.5); background: rgba(var(--v-theme-primary), 0.08); }
  100% { border-color: rgba(var(--v-theme-primary), 0.2); background: rgba(var(--v-theme-primary), 0.03); }
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}

.drag-overlay * {
  pointer-events: none; /* Ensure no child triggers leave events */
}

.medium-article {
  padding-bottom: 100px;
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.story-p {
  font-size: 1.3rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.92);
  margin-bottom: 2.2rem;
  font-weight: 400;
  letter-spacing: -0.01em;
}

.story-h1 { font-size: 2.5rem; font-weight: 800; margin-top: 3.5rem; margin-bottom: 1.5rem; letter-spacing: -0.04em; font-family: 'Lora', serif; }
.story-h2 { font-size: 2rem; font-weight: 700; margin-top: 3rem; margin-bottom: 1.2rem; font-family: 'Lora', serif; }
.story-h3 { font-size: 1.5rem; font-weight: 700; margin-top: 2.5rem; margin-bottom: 1rem; font-family: 'Lora', serif; }

.story-quote {
  border-left: 4px solid var(--v-theme-primary);
  padding-left: 2rem;
  margin: 2rem 0;
}

.story-quote p {
  font-size: 1.8rem;
  font-style: italic;
  opacity: 0.8;
  line-height: 1.4;
}

/* story-code removal - handled by unified system */
.story-code {
  position: relative;
  margin: 2rem 0;
}

.note-feed-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  transition: all 0.2s ease;
  padding: 24px 0;
}

.note-feed-item:hover {
  background: transparent;
}

.note-feed-item h3 {
  transition: color 0.15s ease;
  font-size: 1.5rem !important;
  line-height: 1.25;
}

.note-feed-item:hover h3 {
  color: var(--v-theme-primary);
}

.note-feed-item-header .text-caption {
  font-size: 0.85rem !important;
}

.action-btn {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-btn:hover {
  opacity: 1;
}

.note-feed-thumb :deep(.v-img) {
  background-color: #222 !important;
}

.border-b { border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important; }
.cursor-pointer { cursor: pointer; }
.line-clamp-2 { 
  display: -webkit-box; 
  -webkit-line-clamp: 2; 
  -webkit-box-orient: vertical; 
  overflow: hidden;
  line-clamp: 2;
}
.line-clamp-3 { 
  display: -webkit-box; 
  -webkit-line-clamp: 3; 
  -webkit-box-orient: vertical; 
  overflow: hidden;
  line-clamp: 3;
}

.opacity-30 { opacity: 0.3; }
.opacity-10 { opacity: 0.1; }
.hover-opacity-100:hover { opacity: 1 !important; }

@media (max-width: 800px) {
  .side-controls { position: static; flex-direction: row !important; margin-bottom: 8px; left: auto; }
  .note-title-input :deep(textarea) { font-size: 2.2rem !important; }
  .note-feed-thumb { display: none; }
}

:deep(.v-img),
:deep(.v-img__img),
:deep(.v-img__placeholder),
:deep(.v-responsive) {
  background-color: transparent !important;
}

/* Block Animations */
.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.list-leave-active {
  position: absolute;
  width: 100%;
}

.hover-link:hover {
  text-decoration: underline;
}

.engagement-bar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.engagement-item {
  color: #888;
  transition: color 0.2s;
}

.engagement-count {
  font-size: 0.9rem;
  margin-left: -4px;
}

.engagement-item.active {
  color: var(--v-theme-error);
}

/* Unified Code Block System */
.code-block-container {
  display: flex;
  position: relative;
  width: 100%;
  background: #0d1117;
  border-radius: 8px; /* Slightly more rounded */
  overflow: hidden;
  font-family: 'Fira Code', 'Cascadia Code', 'Source Code Pro', monospace !important;
  font-size: 1.15rem !important; /* Increased for better readability */
  line-height: 1.5 !important;
}

.code-block-container.editor-mode {
  padding: 0;
}

.code-block-container.view-mode {
  padding: 0;
}

.line-numbers {
  padding: 24px 12px; /* Vertical padding here so background covers top/bottom */
  text-align: right;
  color: #4b5563;
  user-select: none;
  background: rgba(255, 255, 255, 0.03);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  min-width: 45px;
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  display: flex;
  flex-direction: column;
}

.view-mode .line-numbers {
  opacity: 0.5;
}

.code-content-wrapper {
  position: relative;
  flex-grow: 1;
  overflow-x: auto;
  padding: 24px 0; /* Vertical padding here */
}

/* Language Badges & Selection */
.code-lang-wrapper {
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 20;
}

.code-lang-label {
  position: absolute;
  top: 12px;
  right: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #aaa;
  z-index: 10;
  letter-spacing: 0.05em;
  pointer-events: none;
}

.code-lang-select {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 140px; /* Slightly wider to fit longer names */
  height: 28px !important;
  display: flex;
  align-items: center;
}

.code-lang-select :deep(.v-field) {
  padding: 0 12px !important;
  min-height: 28px !important;
}

.code-lang-select :deep(.v-field__input) {
  padding: 0 !important;
  min-height: 28px !important;
  display: flex !important;
  align-items: center !important;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  color: #aaa !important;
  letter-spacing: 0.05em !important;
}

.code-lang-select :deep(.v-field__append-inner) {
  padding: 0 !important;
  margin-left: 4px !important;
  display: flex !important;
  align-items: center !important;
}

.editor-textarea, .editor-highlight, .view-mode pre {
  margin: 0 !important;
  padding: 0 16px !important; /* Increased horizontal padding */
  width: 100%;
  min-width: 100%;
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
  tab-size: 2;
  white-space: pre !important;
  word-wrap: normal !important;
  border: none !important;
  display: block;
}

.editor-textarea {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  color: transparent !important;
  background: transparent !important;
  caret-color: white !important;
  resize: none;
  outline: none;
  z-index: 10;
  overflow: hidden;
  -webkit-text-fill-color: transparent;
}

.editor-highlight {
  position: relative;
  z-index: 1;
  pointer-events: none;
  min-height: 3em; 
  background: transparent;
}

.code-block-container code {
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  display: block;
  white-space: pre !important;
  font-family: inherit !important;
  font-size: inherit !important;
  line-height: inherit !important;
}

.drag-handle {
  cursor: grab !important;
  padding: 4px;
  border-radius: 4px;
}

.drag-handle:active {
  cursor: grabbing !important;
}

</style>
